import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import logo from '../../images/logo_dashbo_purple.svg';
import {SignOut} from '../icons';
import {clearOauthReturn} from './oauthReturn';
import {CONTINUE_PATH, isPhoneViewport, MOBILE_EVENTS} from './mobileContinue';
import {Stepper} from './Stepper';
import {ScreenTracker} from '../../utils/ScreenTracker';
import {OnboardingProvider} from './wizardContext';
import './onboarding.scss';
import {CLARITY_UNMASK} from '../../utils/clarity/unmask';

/**
 * El marco del túnel de alta: logo, progreso y la salida.
 *
 * No lleva el menú de la aplicación a propósito. Mientras la agencia arma su cartera no tiene nada
 * que mirar en el resto del producto —sus pantallas están vacías— y ofrecerle el menú es invitarla
 * a irse por la puerta equivocada. El chrome completo aparece recién cuando vuelve, al día
 * siguiente, con su cartera hecha.
 *
 * La salida existe y es visible: "Guardar y seguir después" es lo que hace que quedarse sea una
 * decisión y no un encierro. Lo que el usuario decidió queda guardado y el drawer de primeros pasos
 * le ofrece retomar.
 */
export function OnboardingLayout({step, children}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    // Mientras se crea la cartera no hay nada que «guardar»: la creación ya está en marcha en el
    // servidor y sigue aunque el usuario se vaya. El botón lo dice, para que irse no se lea como
    // abandonar a medias (card t3XCXPg6).
    const creating = useLocation().pathname.startsWith('/onboarding/creando');

    /**
     * La nota de retorno no sobrevive al túnel.
     *
     * Vencía sola a los quince minutos, y ésa es una ventana enorme: si el usuario empieza a
     * conectar una plataforma, cancela y se va a usar la aplicación, CUALQUIER conexión que haga
     * desde la pantalla clásica durante ese rato lo deposita en el túnel —del que ya se fue— y sin
     * los avisos de esa pantalla.
     *
     * El criterio es "dejar el túnel", no un reloj más corto: el reloj sigue siendo una apuesta
     * sobre cuánto tarda una autorización (elegir cuenta + segundo factor + elegir administrador es
     * frecuente), mientras que salir del recorrido es el hecho que de verdad invalida la nota. Se
     * implementa como limpieza del marco porque el marco existe exactamente mientras dura el
     * recorrido: irse a otra pantalla lo desmonta, y navegar entre pasos lo vuelve a montar sin que
     * haya ninguna nota viva que perder (se escribe recién al salir hacia el proveedor, y esa
     * salida es una navegación de página entera que no ejecuta esta limpieza).
     */
    useEffect(() => clearOauthReturn, []);

    const leave = () => {
        // Explícito además de la limpieza de arriba: esta salida no depende de que el desmontaje
        // ocurra, y es la que el usuario usa a propósito.
        clearOauthReturn();
        // En el celular, desde el paso de plataformas, la salida lleva a «Guardamos tu avance»
        // (UD-23, versión del 01/11; la ruta conserva el nombre de la primera): el listado de clientes
        // está vacío para quien no conectó nada, y en las grabaciones la persona volvía a él y se iba
        // en segundos.
        if (step === 'platforms' && isPhoneViewport()) {
            ScreenTracker.recordEvent(MOBILE_EVENTS.exit);
            navigate(CONTINUE_PATH);
            return;
        }
        // Al listado de clientes 2.1, como toda salida del túnel (D8, card HMy64fKi): `/` es el
        // Tablero clásico, y una agencia nueva caía ahí, vacía, a mitad del recorrido.
        navigate('/agency/clients');
    };

    return (
        <OnboardingProvider>
            <div className="ob-tunnel">
                <header className="ob-topbar">
                    <img className="ob-topbar__logo" src={logo} alt="Dashbo"/>
                    {step ? <Stepper current={step}/> : <span className="ob-topbar__spacer"/>}
                    <button type="button" className="ob-exit" onClick={leave}>
                        <SignOut size={15}/>
                        <span {...CLARITY_UNMASK}>{creating ? t('onboarding.exit_background') : t('onboarding.exit')}</span>
                    </button>
                </header>
                <main className="ob-body">{children}</main>
            </div>
        </OnboardingProvider>
    );
}
